import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { takeUrlToken } from "@/lib/urlToken";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

const R = {
  en: { checking: "Verifying your email…", ok: "Email confirmed", okHint: "Your address is verified. You're all set.", bad: "Link invalid or expired", badHint: "Request a new confirmation email from your dashboard.", go: "Go to my space", login: "Log in" },
  fr: { checking: "Vérification de ton email…", ok: "Email confirmé", okHint: "Ton adresse est vérifiée. Tout est prêt.", bad: "Lien invalide ou expiré", badHint: "Demande un nouvel email de confirmation depuis ton espace.", go: "Aller à mon espace", login: "Se connecter" },
};

export default function VerifyEmail() {
  const { lang } = useLang();
  const r = R[lang] || R.en;
  const [sp] = useState(takeUrlToken);
  const { user, setUser } = useAuth();
  const [state, setState] = useState("checking");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const token = sp;
    if (!token) return setState("bad");
    api.post("/auth/verify-email", { token })
      .then(() => { setState("ok"); if (user) setUser({ ...user, email_verified: true }); })
      .catch(() => setState("bad"));
  }, [sp, user, setUser]);

  const dest = user ? (user.role === "club" ? "/club" : "/dashboard") : "/auth?mode=login";
  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-10" data-testid="verify-page">
      <div className="w-full max-w-md rounded-2xl border border-[#1E2638] bg-[#121721] p-8 text-center">
        <div className="text-[10px] font-mono-tech tracking-[0.3em] text-emerald-400">DLSPORT · EMAIL</div>
        {state === "checking" && <div className="mt-6 flex flex-col items-center gap-3 text-slate-400" data-testid="verify-checking"><Loader2 className="w-8 h-8 animate-spin" />{r.checking}</div>}
        {state === "ok" && (
          <div className="mt-6" data-testid="verify-ok">
            <CheckCircle2 className="w-12 h-12 text-[#00F076] mx-auto" />
            <h1 className="font-display font-black uppercase text-3xl mt-3">{r.ok}</h1>
            <p className="text-sm text-slate-400 mt-2">{r.okHint}</p>
          </div>
        )}
        {state === "bad" && (
          <div className="mt-6" data-testid="verify-bad">
            <XCircle className="w-12 h-12 text-[#FF2E54] mx-auto" />
            <h1 className="font-display font-black uppercase text-3xl mt-3">{r.bad}</h1>
            <p className="text-sm text-slate-400 mt-2">{r.badHint}</p>
          </div>
        )}
        {state !== "checking" && (
          <Link to={dest} data-testid="verify-continue" className="inline-block mt-6 bg-[#00F076] hover:bg-[#00C963] text-black font-bold uppercase tracking-wider text-sm px-5 py-2.5 rounded-md">{user ? r.go : r.login}</Link>
        )}
      </div>
    </div>
  );
}
